import { useEffect, useMemo, useState } from "react";
import { UserPlus } from "lucide-react";

import Spinner from "components/Spinner";
import AdminUserCreateModal from "components/admin/users/AdminUserCreateModal";
import { createAdminUser } from "lib/api/createAdminUser";
import { getRoleAdminDashboard } from "lib/api/roleAdmin";
import { useAppStore } from "lib/store/useAppStore";
import {
  formatKoreanDateTime,
  parseUtcDateTime,
} from "lib/utils/koreanDateTime";
import { formatSystemRole } from "lib/utils/roleLabels";

const RECORD_TYPE_LABELS = {
  account_creation: "계정 생성",
  manager_creation_request: "매니저 생성 요청",
};

const RECORD_STATUS_LABELS = {
  pending: "승인 대기",
  approved: "승인 완료",
  rejected: "거절 완료",
};

function formatDate(value) {
  return formatKoreanDateTime(value, { year: "numeric" });
}

function formatLogDate(value) {
  return formatKoreanDateTime(value, {
    sourceTimeZone: "kst",
    year: "numeric",
  });
}

const KOREA_DATE_KEY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Seoul",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function getKoreaDateKey(value) {
  return KOREA_DATE_KEY_FORMATTER.format(value);
}

function getRecordStatusLabel(record) {
  if (record?.type === "account_creation") return "생성 완료";
  return RECORD_STATUS_LABELS[record?.status] ?? record?.status ?? "-";
}

function getRecordStatusClass(record) {
  if (record?.type === "account_creation") return "approved";
  return record?.status ?? "pending";
}

function formatRecentActivity(account) {
  if (account?.latest_activity_title) return account.latest_activity_title;
  if (account?.latest_activity_message) return account.latest_activity_message;
  if (!account?.last_login) return "로그인 기록 없음";

  const loginDate = parseUtcDateTime(account.last_login);
  if (!loginDate) return "최근 로그인";

  const now = new Date();
  if (getKoreaDateKey(loginDate) === getKoreaDateKey(now)) {
    return "오늘 접속";
  }

  return "최근 로그인";
}

function EmptyTableRow({ colSpan, children }) {
  return (
    <tr>
      <td className="roleAdminTable__empty" colSpan={colSpan}>
        {children}
      </td>
    </tr>
  );
}

export default function AdminOperatorHomePage() {
  const showAlert = useAppStore((s) => s.showAlert);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openCreate, setOpenCreate] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      setIsLoading(true);
      const result = await getRoleAdminDashboard();

      if (!alive) return;

      if (!result.success) {
        setErrorMessage(result.message || "관리자 센터를 불러오지 못했습니다.");
        setData(null);
        setIsLoading(false);
        return;
      }

      setData(result.data ?? {});
      setErrorMessage("");
      setIsLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, []);

  const refreshDashboard = async () => {
    const result = await getRoleAdminDashboard();
    if (result.success) {
      setData(result.data ?? {});
      setErrorMessage("");
    }
  };

  const handleCreateUser = async (payload) => {
    const result = await createAdminUser(payload);
    const detailMessage =
      result.code === "USER_CREATE_LIMIT_EXCEEDED" && result.data
        ? `${result.message} (${result.data.userCreateUsed ?? 0} / ${
            result.data.userCreateLimit ?? 0
          })`
        : result.code
          ? `${result.message} [${result.code}]`
          : result.message;

    showAlert({
      text: detailMessage || "계정 생성 요청을 처리했습니다.",
      success: result.success,
      autoClose: true,
    });

    if (result.success) {
      setOpenCreate(false);
      await refreshDashboard();
    }
  };

  const summaryItems = useMemo(() => {
    const summary = data?.summary ?? {};
    const userCreateLimit = Number(summary.userCreateLimit ?? 0);
    const userCreateUsed = Number(summary.userCreateUsed ?? 0);

    return [
      {
        label: "관리 중인 사용자",
        value: summary.createdAccounts ?? 0,
      },
      {
        label: "생성 한도",
        value: `${userCreateUsed} / ${userCreateLimit}`,
      },
    ];
  }, [data?.summary]);

  if (isLoading) {
    return (
      <section className="content admin roleAdminPage">
        <Spinner />
      </section>
    );
  }

  if (errorMessage) {
    return (
      <section className="content admin roleAdminPage">
        <div className="roleAdminHeader">
          <h2>관리자 센터</h2>
        </div>
        <div className="roleAdminEmpty">{errorMessage}</div>
      </section>
    );
  }

  const accounts = data?.accounts ?? [];
  const recentRecords = data?.recentRecords ?? [];
  const recentActivities = data?.recentActivities ?? [];

  return (
    <section className="content admin roleAdminPage">
      <div className="roleAdminHeader">
        <div>
          <h2>관리자 센터</h2>
        </div>
      </div>

      <div className="roleAdminMetricGrid">
        {summaryItems.map((item) => (
          <div className="roleAdminMetric" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
        <button
          className="roleAdminCreateButton"
          onClick={() => setOpenCreate(true)}
          type="button"
        >
          <UserPlus size={18} aria-hidden="true" />
          <span>계정 생성</span>
        </button>
      </div>

      <section className="roleAdminPanel">
        <div className="roleAdminPanel__header">
          <h3>관리 중인 사용자</h3>
        </div>
        <div className="roleAdminTableWrap">
          <table className="roleAdminTable roleAdminTable--users">
            <thead>
              <tr>
                <th>이름</th>
                <th>이메일</th>
                <th>소속 조직</th>
                <th>권한</th>
                <th>최근 로그인</th>
                <th>최근 활동</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.user_name || "-"}</td>
                  <td>{account.email || "-"}</td>
                  <td>{account.organization_name || account.company_name || "-"}</td>
                  <td>
                    {formatSystemRole(account.role || account.organization_role)}
                  </td>
                  <td>{formatDate(account.last_login)}</td>
                  <td>{formatRecentActivity(account)}</td>
                </tr>
              ))}
              {accounts.length === 0 ? (
                <EmptyTableRow colSpan={6}>관리 중인 사용자가 없습니다.</EmptyTableRow>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="roleAdminPanel">
        <div className="roleAdminPanel__header">
          <h3>최근 기록</h3>
        </div>
        <div className="roleAdminTableWrap">
          <table className="roleAdminTable roleAdminTable--records">
            <thead>
              <tr>
                <th>구분</th>
                <th>계정</th>
                <th>권한</th>
                <th>상태</th>
                <th>기록일</th>
              </tr>
            </thead>
            <tbody>
              {recentRecords.map((record) => (
                <tr key={`${record.type}-${record.id}`}>
                  <td>{RECORD_TYPE_LABELS[record.type] ?? "계정 기록"}</td>
                  <td>
                    {record.user_name || "-"} / {record.email || "-"}
                  </td>
                  <td>{formatSystemRole(record.role)}</td>
                  <td>
                    <span
                      className={`roleAdminStatus roleAdminStatus--${getRecordStatusClass(record)}`}
                    >
                      {getRecordStatusLabel(record)}
                    </span>
                  </td>
                  <td>{formatDate(record.created_at)}</td>
                </tr>
              ))}
              {recentRecords.length === 0 ? (
                <EmptyTableRow colSpan={5}>최근 기록이 없습니다.</EmptyTableRow>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="roleAdminPanel">
        <div className="roleAdminPanel__header">
          <h3>최근 활동</h3>
        </div>
        <div className="roleAdminTableWrap">
          <table className="roleAdminTable roleAdminTable--activity">
            <thead>
              <tr>
                <th>사용자</th>
                <th>활동</th>
                <th>시간</th>
              </tr>
            </thead>
            <tbody>
              {recentActivities.map((activity) => (
                <tr key={activity.id}>
                  <td>{activity.user_name || activity.email || "-"}</td>
                  <td>{activity.api_title || activity.log_message || "-"}</td>
                  <td>{formatLogDate(activity.created_at)}</td>
                </tr>
              ))}
              {recentActivities.length === 0 ? (
                <EmptyTableRow colSpan={3}>최근 활동이 없습니다.</EmptyTableRow>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <AdminUserCreateModal
        defaultRole="user"
        onClose={() => setOpenCreate(false)}
        onCreate={handleCreateUser}
        open={openCreate}
      />
    </section>
  );
}
